// @flow
import types from "./types";
import actions from "./actions";
import selectors from "./selectors";
import type {Dispatch} from "redux";
import {ServerProposalStatus, ServerRideStatus} from "../../../Utils/AppUtils";
import AppOperations from "../App/operations";
import MapDataOperations from "../MapData/operations";
import {strings} from "../../../config";
import type {GetState} from "../../Store";
import CurrentProposalTypes from "../CurrentProposal/types";
import BackendOperations from "../../Middleware/Backend/operations";
import BackendTypes from "../../Middleware/Backend/types";
import {ActiveServerRideStatuses} from "./utils";
import type {DriverPhoneData} from "./utils";
import {registerMiddleman} from "../../Middleware/Middleman/middleware";
import { MapPOI } from "../MapData/utils";

const rideDataUpdate = (rideData: Object) => {
    return (dispatch: Dispatch, getState: GetState) => {
        dispatch(actions.rideDataUpdate(rideData));
    };
};

const _handleRideStatusCancelled = (rideData: Object) => {
    return (dispatch: Dispatch, getState: GetState) => {
        let state = getState();
        if (!selectors.isConcreteRide(state)) {
            return dispatch(AppOperations.noOperation());
        }
        return dispatch(AppOperations.showAlert({
            title: strings.alert_message_ride_was_cancelled_title_text,
            content: strings.alert_message_ride_was_cancelled_content_text,
            buttons: [{
                text: strings.alert_window_generic_accept_button_text, actions:
                    [BackendTypes.MIDDLEMAN_INVALIDATE_REQUEST, types.RIDE_DATA_CLEAR]
            }]
        }));
    };
};
const _handleRideStatusNoShow = (rideData: Object) => {
    return (dispatch: Dispatch, getState: GetState) => {
        let state = getState();
        if (!selectors.isConcreteRide(state)) {
            return dispatch(AppOperations.noOperation());
        }
        return dispatch(AppOperations.showAlert({
            title: strings.alert_message_no_show_title_text,
            content: strings.alert_message_no_show_content_text,
            buttons: [{
                text: strings.alert_window_generic_accept_button_text, actions:
                    [BackendTypes.MIDDLEMAN_INVALIDATE_REQUEST, types.RIDE_DATA_CLEAR]
            }]
        }));
    };
};
const _handleRideStatusFinished = (rideData: Object) => {
    return (dispatch: Dispatch, getState: GetState) => {
        let state = getState();
        if (!selectors.isConcreteRide(state)) {
            return dispatch(AppOperations.noOperation());
        }
        dispatch(rideDataUpdate(rideData));
    };
};

const _rideStatusHandlers = {
    [ServerRideStatus.ACCEPTED]: rideDataUpdate,
    [ServerRideStatus.BOARDED]: rideDataUpdate,
    [ServerRideStatus.CANCELED]: _handleRideStatusCancelled,
    [ServerRideStatus.NO_SHOW]: _handleRideStatusNoShow,
    [ServerRideStatus.FINISHED]: _handleRideStatusFinished,
    [ServerRideStatus.FEEDBACK]: _handleRideStatusFinished,
};

// Todo(Oded): argument type should be whatever the server returns for /last/ride/status
const updateRideDataSuccess = (rideData: Object) => {
    return (dispatch: Dispatch, getState: GetState) => {
        dispatch(_rideStatusHandlers[rideData.ride_status](rideData));
    };
};

const updateStartupRideDataSuccess = (rideData: Object) => {
    return (dispatch: Dispatch, getState: GetState) => {
        if (ActiveServerRideStatuses.includes(rideData.ride_status)) {
            dispatch(_rideStatusHandlers[rideData.ride_status](rideData));

            const boundsPoints = [];
            if (rideData.pickup_location) { boundsPoints.push(MapPOI.PICKUP); }
            if (rideData.dropoff_location) { boundsPoints.push(MapPOI.DROPOFF); }
            if (rideData.vehicle_location) { boundsPoints.push(MapPOI.VAN); }
            if (boundsPoints.length > 0) {
                dispatch(MapDataOperations.focusMapBounds(boundsPoints));
            }
        } else {
            dispatch(AppOperations.noOperation());
        }
    };
};

const proposalStatusSuccess = (proposalStatus: Object) => {
    return (dispatch: Dispatch, getState: GetState) => {
        if (proposalStatus.proposal_status === ServerProposalStatus.ACCEPTED) {
            dispatch(rideDataUpdate({...proposalStatus, awaitingDriverApproval: false}));
        } else if (proposalStatus.proposal_status === ServerProposalStatus.REJECTED) {
            dispatch(AppOperations.showAlert({
                title: strings.alert_window_generic_error_title_text,
                content: proposalStatus.rejection_reason,
                buttons: [{
                    text: strings.alert_window_generic_accept_button_text,
                    actions: [types.RIDE_DATA_CLEAR, BackendTypes.MIDDLEMAN_INVALIDATE_REQUEST]
                }]
            }));
        }
        dispatch(AppOperations.noOperation());
    };
};

const rideDataClear = actions.rideDataClear;

const _cancelRide = () => {
    return (dispatch: Dispatch, getState: GetState) => {
        return dispatch(BackendOperations.doGetCancellationFee());
    };
};

const _cancelProposal = (actions: Array<string>) => {
    return (dispatch: Dispatch, getState: GetState) => {
        return dispatch(AppOperations.showAlert({
            title: strings.alert_message_cancel_ride_title_text,
            content: strings.alert_message_cancel_proposal_text,
            buttons: [
                {text: strings.alert_message_cancel_ride_cancel_text},
                {
                    text: strings.alert_message_cancel_ride_confirm_text,
                    actions: actions
                }
            ]
        }));
    };
};


const _cancelAwaitingDriverProposal = () => {
    return (dispatch: Dispatch, getState: GetState) => {
        return dispatch(_cancelProposal([
            BackendTypes.MIDDLEMAN_CANCEL_PROPOSAL
        ]));
    };
};

const _cancelRegularProposal = () => {
    return (dispatch: Dispatch, getState: GetState) => {
        return dispatch(_cancelProposal([
            BackendTypes.MIDDLEMAN_INVALIDATE_REQUEST,
            CurrentProposalTypes.PROPOSAL_DATA_CLEAR
        ]));
    };
};

const cancelRideOrProposalClick = () => {
    return (dispatch: Dispatch, getState: GetState) => {
        const state = getState();
        const isConcreteRide = selectors.isConcreteRide(state);
        const isAwaitingDriver = selectors.currentRideSelector(state).awaitingDriverApproval;
        if (isConcreteRide) {
            if (isAwaitingDriver) {
                return dispatch(_cancelAwaitingDriverProposal());
            } else {
                return dispatch(_cancelRide());
            }
        } else {
            return dispatch(_cancelRegularProposal());
        }
    };
};

const handleWalkingMapRequestSuccess = actions.handleWalkingMapRequestSuccess;
const requestedWalkingMap = actions.requestedWalkingMap;


const failGettingDrivePhone = (error?: Object) => {
    return (dispatch: Dispatch, getstate: GetState) => {
        return dispatch(AppOperations.showAlert({content: strings.alert_message_call_driver_failure_content_text}));
    };
};


const setDriverPhone = actions.setDriverPhone;

const successGettingDriverPhone = (driverPhoneData: DriverPhoneData) => {
    return (dispatch: Dispatch, getstate: GetState) => {
        if (!driverPhoneData.driver_phone) {
            return dispatch(failGettingDrivePhone());
        }
        return dispatch(AppOperations.showAlert({
            title: strings.alert_message_call_driver_title_text,
            content: strings.alert_message_call_driver_content_text,
            buttons: [
                {
                    text: strings.alert_message_call_driver_call_text,
                    actions: [types.MIDDLEMAN_CALL_DRIVER]
                }, {
                    text: strings.alert_message_call_driver_cancel_text,
                }
            ]
        }));
    };
};

const callDriver = () => {
    return (dispatch: Dispatch, getState: GetState) => {
        dispatch(AppOperations.noOperation());
        const state = getState();
        const driverPhone = selectors.getDriverPhone(state);
        window.location.href = "tel://" + driverPhone;
    };
};
registerMiddleman(types.MIDDLEMAN_CALL_DRIVER, callDriver);

export default {
    updateRideDataSuccess,
    updateStartupRideDataSuccess,
    proposalStatusSuccess,
    rideDataClear,
    cancelRideOrProposalClick,
    rideDataUpdate,
    handleWalkingMapRequestSuccess,
    requestedWalkingMap,
    setDriverPhone,
    successGettingDriverPhone,
    failGettingDrivePhone,
    callDriver,
};



// WEBPACK FOOTER //
// ./src/Store/Ducks/CurrentRide/operations.js